import { createContext, useContext, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, XCircle, Info, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: string
  type: ToastType
  message: string
  description?: string
}

interface ToastCtx {
  toast: (msg: string, opts?: { type?: ToastType; description?: string }) => void
  success: (msg: string, description?: string) => void
  error: (msg: string, description?: string) => void
  info: (msg: string, description?: string) => void
}

const ToastContext = createContext<ToastCtx | null>(null)

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within Toaster')
  return ctx
}

const ICONS = {
  success: <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />,
  error: <XCircle className="w-4 h-4 text-red-400 shrink-0" />,
  info: <Info className="w-4 h-4 text-accent shrink-0" />,
}

const GLOW: Record<ToastType, string> = {
  success: 'glow-green border-emerald-500/30',
  error: 'glow-red border-red-500/30',
  info: 'glow-cyan border-accent/30',
}

export function Toaster({ children }: { children?: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((message: string, opts?: { type?: ToastType; description?: string }) => {
    const id = Math.random().toString(36).slice(2)
    const type = opts?.type ?? 'info'
    setToasts(prev => [...prev, { id, type, message, description: opts?.description }])
    setTimeout(() => dismiss(id), 4000)
  }, [dismiss])

  const ctx: ToastCtx = {
    toast: addToast,
    success: (msg, desc) => addToast(msg, { type: 'success', description: desc }),
    error: (msg, desc) => addToast(msg, { type: 'error', description: desc }),
    info: (msg, desc) => addToast(msg, { type: 'info', description: desc }),
  }

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              drag="x"
              dragConstraints={{ left: 0, right: 100 }}
              onDragEnd={(_, info) => { if (info.offset.x > 60) dismiss(t.id) }}
              className={`glass-card pointer-events-auto flex items-start gap-3 px-4 py-3 min-w-[280px] max-w-sm cursor-grab active:cursor-grabbing border ${GLOW[t.type]}`}
            >
              {ICONS[t.type]}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">{t.message}</p>
                {t.description && <p className="text-xs text-muted mt-0.5">{t.description}</p>}
              </div>
              <button onClick={() => dismiss(t.id)} className="text-muted hover:text-foreground transition-colors shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}
